import { useMemo, useState } from 'react';

import { Nut, OTimKiem } from '../../../ui/index.js';

/** Từ bao nhiêu kênh trở lên thì hiện ô tìm và hai nút chọn nhanh. */
export const NGUONG_TIM_KENH = 8;

export interface ONhieuKenhProps {
  kenh: { id: string; name: string }[];
  chon: string[];
  onDoi: (id: string) => void;
  /** Đặt lại trọn danh sách — dùng cho "Chọn tất cả" / "Bỏ chọn". */
  onDatLai: (ids: string[]) => void;
}

/**
 * Ô chọn kênh được giao: danh sách tick trong khung cuộn cao cố định.
 *
 * Quá `NGUONG_TIM_KENH` kênh thì thêm ô tìm và hai nút "Chọn tất cả" / "Bỏ
 * chọn" — cuộn tay qua vài chục kênh để tìm một cái tên là việc vô ích.
 *
 * Hai nút chọn nhanh áp lên **danh sách đang lọc**, không phải toàn bộ: gõ
 * "ambient" rồi bấm "Chọn tất cả" là chọn đúng mấy kênh ambient, giữ nguyên lựa
 * chọn ở các kênh khác. Nhãn nút nói rõ số lượng để không ai hiểu nhầm.
 */
export function ONhieuKenh({ kenh, chon, onDoi, onDatLai }: ONhieuKenhProps) {
  const [tim, setTim] = useState('');
  const nhieu = kenh.length > NGUONG_TIM_KENH;

  const locRa = useMemo(() => {
    const q = tim.trim().toLowerCase();
    return q ? kenh.filter((k) => k.name.toLowerCase().includes(q)) : kenh;
  }, [kenh, tim]);

  const idLoc = locRa.map((k) => k.id);
  const daChonHet = idLoc.length > 0 && idLoc.every((id) => chon.includes(id));

  if (kenh.length === 0) {
    return <p className="text-phu text-chu-mo">Chưa có kênh nào để giao.</p>;
  }

  return (
    <>
      {nhieu && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <OTimKiem
            value={tim}
            onChange={setTim}
            placeholder="Tìm kênh"
            className="min-w-[180px] flex-1"
          />
          <Nut
            kieu="nhe"
            onClick={() => {
              onDatLai(
                daChonHet
                  ? chon.filter((id) => !idLoc.includes(id))
                  : [...new Set([...chon, ...idLoc])],
              );
            }}
          >
            {daChonHet ? `Bỏ chọn ${String(idLoc.length)}` : `Chọn tất cả ${String(idLoc.length)}`}
          </Nut>
        </div>
      )}

      {locRa.length === 0 ? (
        <p className="text-phu text-chu-mo">Không có kênh nào khớp &quot;{tim}&quot;.</p>
      ) : (
        <ul className="max-h-56 space-y-1.5 overflow-y-auto rounded-[10px] border border-vien p-2">
          {locRa.map((k) => (
            <li key={k.id}>
              <label className="flex items-center gap-3 text-co-ban text-chu">
                <input
                  type="checkbox"
                  className="size-4 shrink-0"
                  checked={chon.includes(k.id)}
                  onChange={() => {
                    onDoi(k.id);
                  }}
                />
                <span className="truncate">{k.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {/* Một chuỗi liền, không ghép nhiều text node: React chèn dấu phân cách vào
          giữa và câu bị cắt vụn khi đọc HTML (test hay chính trình đọc màn hình). */}
      <p className="mt-2 text-phu text-chu-mo">
        {`Đã chọn ${String(chon.length)} / ${String(kenh.length)} kênh.`}
      </p>
    </>
  );
}
